import React, { useState } from 'react';
import { X } from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Form submission handler using Web3Forms (A free service for sending emails)
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    // Note for the user: You will need to replace this key with your own from web3forms.com
    formData.append('access_key', 'bf518074-0994-44e7-8676-f17879962644');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSuccess(true);
        // Optional: Close modal after a few seconds
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 3000);
      } else {
        alert('Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error(error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity'>
      <div
        className='relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className='absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors'
          aria-label='Close modal'
        >
          <X size={20} />
        </button>

        <div className='p-8'>
          <h2 className='text-3xl font-bold text-gray-900 mb-2'>Get in Touch</h2>
          <p className='text-gray-600 mb-6'>Fill out the form below and we'll get back to you shortly.</p>

          {success ? (
            <div className='p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50'>
              <span className='font-medium'>Success!</span> Your message has been sent successfully.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='space-y-4'>
              {/* Spam Protection for Web3Forms */}
              <input type='checkbox' name='botcheck' className='hidden' style={{ display: 'none' }} />

              <div>
                <label htmlFor='name' className='block text-sm font-medium text-gray-700 mb-1'>Full Name</label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
                  placeholder='John Doe'
                />
              </div>

              <div>
                <label htmlFor='phone' className='block text-sm font-medium text-gray-700 mb-1'>Mobile Number</label>
                <input
                  type='tel'
                  id='phone'
                  name='phone'
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
                  placeholder='+1 (555) 000-0000'
                />
              </div>

              <div>
                <label htmlFor='email' className='block text-sm font-medium text-gray-700 mb-1'>Email Address</label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
                  placeholder='john@example.com'
                />
              </div>

              <button
                type='submit'
                disabled={isSubmitting}
                className='w-full py-3 px-4 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed'
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
